import { useState } from "react";
import { STANDARD_CATEGORY_HIERARCHY, STANDARD_CATEGORY_LABEL, type DocumentRecord, type Role } from "@shared/types";
import { SH82_CHECKSUM } from "@shared/sh82Checksum";

const STATUS_CHIP: Record<DocumentRecord["status"], string> = {
  draft: "status-draft",
  approved: "status-approved",
  archived: "status-archived",
};

interface DocumentsTableProps {
  documents: DocumentRecord[];
  role: Role | null;
  busyId: string | null;
  onReextract?: (doc: DocumentRecord) => void;
  onApprove?: (doc: DocumentRecord) => void;
  onArchive?: (doc: DocumentRecord) => void;
  onUnarchive?: (doc: DocumentRecord) => void;
  onDelete?: (doc: DocumentRecord) => void;
  /** Opens the draft-table review modal for this document — shown whenever pendingTableReviewCount > 0, independent of the document's own approval status (an already-approved document can still have agent-drafted tables awaiting their own separate review; see db/migrations/063_agent_drafted_table_review.sql). */
  onReviewTables?: (doc: DocumentRecord) => void;
  emptyMessage?: string;
  /** Groups rows under a subheading per standardCategory (in STANDARD_CATEGORY_HIERARCHY order), instead of one flat list. Off by default — additive for callers like SourcesPage that want it. */
  groupByCategory?: boolean;
}

/** Shared document list table — used by SourcesPage (project + shared statewide docs) and ContractDocsPage (this project's own docs only) so both stay in sync on columns, status styling, and the owner-only Approve/Archive/Unarchive/Delete actions. */
export function DocumentsTable({
  documents,
  role,
  busyId,
  onReextract,
  onApprove,
  onArchive,
  onUnarchive,
  onDelete,
  onReviewTables,
  emptyMessage,
  groupByCategory,
}: DocumentsTableProps) {
  const showActions = role === "owner" && (onReextract || onApprove || onArchive || onUnarchive || onDelete || onReviewTables);
  const colCount = showActions ? 6 : 5;
  const archivedCount = documents.filter((d) => d.status === "archived").length;
  // The archived folder is opt-in and separate from the main list: an
  // archived document has no bearing on day-to-day work (it's already
  // excluded from retrieval), so it shouldn't crowd the table everyone
  // actually needs to scan every time. Category grouping only applies to
  // the active view — the archived folder is a flat parking lot, not
  // something that needs its own taxonomy browsing.
  const [showArchived, setShowArchived] = useState(false);
  const visibleDocuments = documents.filter((d) => (showArchived ? d.status === "archived" : d.status !== "archived"));

  function handleArchive(doc: DocumentRecord) {
    if (!onArchive) return;
    if (!window.confirm(`Archive "${doc.title}"? It will stop being used to answer questions immediately.`)) return;
    onArchive(doc);
  }

  function handleUnarchive(doc: DocumentRecord) {
    if (!onUnarchive) return;
    if (!window.confirm(`Restore "${doc.title}" to Approved? It will be used to answer questions again immediately.`)) return;
    onUnarchive(doc);
  }

  function handleDelete(doc: DocumentRecord) {
    if (!onDelete) return;
    const warning =
      doc.status === "archived"
        ? `Permanently delete "${doc.title}"? This document was previously approved — if any past answer cited it, that citation's "View PDF"/"Show table" link will stop working. This cannot be undone.`
        : `Permanently delete "${doc.title}"? This removes the file and cannot be undone.`;
    if (!window.confirm(warning)) return;
    onDelete(doc);
  }

  function renderRow(doc: DocumentRecord) {
    return (
      <tr key={doc.id}>
        <td>
          <span className="doc-title">{doc.title}</span>
          {doc.projectId === null && <span className="doc-shared"> (shared)</span>}
        </td>
        <td className="doc-type">{doc.sourceType.replace(/_/g, " ")}</td>
        <td className="doc-version mono">v{doc.version}</td>
        <td>
          <span className={`status-chip ${STATUS_CHIP[doc.status]}`}>{doc.status}</span>
        </td>
        <td className="ingest-status">
          {doc.ingestStatus}
          {doc.sourceType === "contract_plans" && (
            doc.pageCoverage ? (
              <div className="doc-page-coverage">
                <div>{doc.pageCoverage.inventoriedPages}{doc.pageCount === null ? "" : `/${doc.pageCount}`} pages inventoried; {doc.pageCoverage.ocrSucceededPages} OCR complete</div>
                <div>{doc.pageCoverage.structuredPages} structured; {doc.pageCoverage.unstructuredPages} without structured interpretation</div>
                {doc.pageCount !== null && doc.pageCoverage.inventoriedPages < doc.pageCount && (
                  <div className="error-text">{doc.pageCount - doc.pageCoverage.inventoriedPages} pages missing from inventory</div>
                )}
              </div>
            ) : <div className="doc-page-coverage">Page coverage unavailable</div>
          )}
          {doc.ingestError && <div className="error-text">{doc.ingestError}</div>}
        </td>
        {showActions && (
          <td className="doc-actions">
            {/* This does not edit the document in place: it creates a NEW draft
                replacement and re-runs the whole Azure extraction, leaving this
                version live until the replacement is approved. The label and the
                confirmation in ContractDocsPage say so, because "Reprocess with
                Azure" read as an in-place repair and was clicked three times in a
                row against an already-repaired document. */}
            {onReextract && doc.status === "approved" && doc.mimeType === "application/pdf" && (
              <button className="btn" disabled={busyId === doc.id} onClick={() => onReextract(doc)}>
                {busyId === doc.id ? "Queuing…" : "Replace via Azure…"}
              </button>
            )}
            {onReextract && doc.status === 'draft' && ['ready','failed'].includes(doc.ingestStatus) && doc.checksum === SH82_CHECKSUM && (
              <button className="btn" disabled={busyId === doc.id} onClick={() => onReextract(doc)}>
                {busyId === doc.id ? 'Queuing…' : 'Repair reviewed tables'}
              </button>
            )}
            {onApprove && doc.status === "draft" && doc.ingestStatus === "ready" && (
              <button className="btn" disabled={busyId === doc.id} onClick={() => onApprove(doc)}>
                {busyId === doc.id ? "Approving…" : "Approve"}
              </button>
            )}
            {onReviewTables && !!doc.pendingTableReviewCount && (
              <button className="btn btn-review-tables" onClick={() => onReviewTables(doc)}>
                {doc.pendingTableReviewCount} table{doc.pendingTableReviewCount === 1 ? "" : "s"} need{doc.pendingTableReviewCount === 1 ? "s" : ""} review
              </button>
            )}
            {/* A draft can always be permanently deleted (never eligible for retrieval, so never
                cited by a past answer). An archived doc can also be permanently deleted — but only
                after explicit confirmation, since it may have been approved in the past and have
                real historical citations; see documents-delete.ts and handleDelete's warning above. */}
            {onDelete && (doc.status === "draft" || doc.status === "archived") && (
              <button className="btn btn-danger" disabled={busyId === doc.id} onClick={() => handleDelete(doc)}>
                {busyId === doc.id ? "Deleting…" : "Delete"}
              </button>
            )}
            {onArchive && doc.status === "approved" && (
              <button className="btn btn-danger" disabled={busyId === doc.id} onClick={() => handleArchive(doc)}>
                {busyId === doc.id ? "Archiving…" : "Archive"}
              </button>
            )}
            {onUnarchive && doc.status === "archived" && (
              <button className="btn" disabled={busyId === doc.id} onClick={() => handleUnarchive(doc)}>
                {busyId === doc.id ? "Restoring…" : "Unarchive"}
              </button>
            )}
          </td>
        )}
      </tr>
    );
  }

  const groups =
    groupByCategory && !showArchived
      ? STANDARD_CATEGORY_HIERARCHY.map((category) => ({
          category,
          docs: visibleDocuments.filter((d) => d.standardCategory === category),
        })).filter((g) => g.docs.length > 0)
      : null;

  return (
    <div>
      {(archivedCount > 0 || showArchived) && (
        <div className="archived-toggle-row">
          <button type="button" className="archived-toggle" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? "← Back to active documents" : `View archived (${archivedCount})`}
          </button>
        </div>
      )}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Version</th>
              <th>Status</th>
              <th>Ingest</th>
              {showActions && <th></th>}
            </tr>
          </thead>
          {groups ? (
            groups.map(({ category, docs }) => (
              <tbody key={category}>
                <tr className="table-group-row">
                  <td colSpan={colCount}>
                    {STANDARD_CATEGORY_LABEL[category]} <span className="table-group-count">({docs.length})</span>
                  </td>
                </tr>
                {docs.map(renderRow)}
              </tbody>
            ))
          ) : (
            <tbody>{visibleDocuments.map(renderRow)}</tbody>
          )}
          {visibleDocuments.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={colCount} className="muted">
                  {showArchived ? "No archived documents." : (emptyMessage ?? "No sources yet.")}
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
}
